import Bookmark from "@untitled-ui/icons-react/build/esm/Bookmark";
import FilterLines from "@untitled-ui/icons-react/build/esm/FilterLines";
import SearchSm from "@untitled-ui/icons-react/build/esm/SearchSm";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useRef, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchHistory } from "../../../../hooks";
import { saveSearch } from "../../../../services/post-save-search";
import {
  doSearch,
  SEARCH_TYPES,
  setCurrentInputQuery,
  setQueryValue as setQueryValueRedux,
  setSearchType,
} from "../../../../store/reducer/_searchReducer";
import {
  addError,
  showFilterModal,
} from "../../../../store/reducer/_uiReducer";
import {
  getAppliedFilters,
  getCurrentInputQueryValue,
  getCurrentModelTypeAppliedFilters,
  getCurrentModelTypeFilters,
  getCurrentSearchPeriod,
  getPageTitle,
  getSearchModelType,
  getSearchResultsType,
  getSearchType,
  getSelectedPersonFilter,
} from "../../../../store/selectors";
import { openChatWithTerm } from "../../../../utils";
import {
  getStoredSearchQuery,
  getStoredSearchType,
  getStoredToken,
} from "../../../../utils/_localStorage";
import { notify } from "../../../../utils/_notify";
import { ErrorMessage } from "../../../../utils/classes";
import { PDLButton } from "../../dna";
import { ChatIaAnimatedButton } from "../_chatIaAnimatedButton";
import { SearchHistoryDropdown } from "../_searchHistoryDropdown";
import "./index.scss";

const OmniBar = ({
  headerClass = null,
  isMobile = false,
  showSaveSearchButton = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const searchType = useSelector(getSearchType);
  const pageTitle = useSelector(getPageTitle);
  const currentQueryValue = useSelector(getCurrentInputQueryValue);

  const appliedFilters = useSelector(getAppliedFilters);
  const modalTypeAppliedFilters = useSelector(
    getCurrentModelTypeAppliedFilters,
  );
  const currentModelTypeFilters = useSelector(getCurrentModelTypeFilters);
  const appliedModelType = useSelector(getSearchModelType);
  const currentPeriod = useSelector(getCurrentSearchPeriod);
  const selectedPersonFilter = useSelector(getSelectedPersonFilter);
  const searchResultsType = useSelector(getSearchResultsType);

  const [queryValue, setQueryValue] = useState(() => {
    const storedQuery = getStoredSearchQuery();
    return currentQueryValue || storedQuery || "";
  });
  const [showHistory, setShowHistory] = useState(false);
  const [isSavingSearch, setIsSavingSearch] = useState(false);

  const debounceRef = useRef(null);
  const lastSyncedReduxValue = useRef(currentQueryValue);

  const token = getStoredToken();
  const decodedToken = jwtDecode(token);
  const chatEnabled = decodedToken?.chat;

  const { addToHistory, removeFromHistory, clearHistory, getFilteredHistory } =
    useSearchHistory();

  const history = getFilteredHistory(searchType?.id);

  const wrapperRef = useRef(null);

  const handleDDChange = useCallback(
    (value) => {
      dispatch(setSearchType(value));
    },
    [dispatch],
  );

  const handleInputChange = useCallback(({ target: { value = "" } = {} }) => {
    setQueryValue(value);
    setShowHistory(value?.length > 0);
  }, []);

  const handleSearch = useCallback(() => {
    if (
      queryValue?.length > 2 ||
      (searchType.id === "legislacao" && appliedFilters.legislacao.length >= 2)
    ) {
      dispatch(setQueryValueRedux(queryValue));
      addToHistory(queryValue, searchType.id);
      dispatch(doSearch({ query: queryValue, searchType }));
      navigate("/resultados");
    } else {
      const errorMessage = new ErrorMessage({
        message: "A pesquisa deve conter mais de 2 caracteres.",
        title: "Pesquisa Inválida",
        variant: "warning",
      });
      dispatch(addError(errorMessage));
    }
  }, [
    dispatch,
    navigate,
    queryValue,
    searchType,
    addToHistory,
    appliedFilters,
  ]);

  const toggleFilterModal = useCallback(() => {
    dispatch(showFilterModal());
  }, [dispatch]);

  const handleSaveSearch = useCallback(async () => {
    if (!currentQueryValue || currentQueryValue.trim().length === 0) {
      const errorMessage = new ErrorMessage({
        message: "Nenhuma pesquisa ativa para salvar.",
        title: "Pesquisa Inválida",
        variant: "warning",
      });
      dispatch(addError(errorMessage));
      return;
    }

    setIsSavingSearch(true);

    try {
      const collectionType =
        searchType?.collectionType || searchResultsType || "Jurisprudencia";

      const filters = {};
      const currentSearchTypeId = searchType?.id;

      if (currentSearchTypeId === "modelos") {
        if (appliedModelType && appliedModelType !== "all") {
          filters.tipoModelo = appliedModelType;
        }

        const appliedValues = modalTypeAppliedFilters || [];
        const modelFilters = currentModelTypeFilters || [];

        modelFilters.forEach(({ id, options = [] }) => {
          const selected = options
            .filter((opt) => opt.checked && appliedValues.includes(opt.label))
            .map((opt) => opt.label);

          if (selected.length) {
            filters[id] = selected.join(",");
          }
        });
      }

      const appliedValues =
        appliedFilters?.[currentSearchTypeId || "all"] || [];
      appliedValues.forEach(({ id, values = [] }) => {
        if (id && values.length) {
          filters[id] = values.join(",");
        }
      });

      const { period, startDate, endDate } = currentPeriod || {};

      if (searchType?.personFilter?.param && selectedPersonFilter) {
        filters[searchType.personFilter.param] = selectedPersonFilter;
      }

      if (period && period !== "always") {
        if (startDate) filters.dataInicio = startDate;
        if (endDate) filters.dataFim = endDate;
      }

      const params = {
        q: currentQueryValue,
        limit: 10,
        page: 1,
        sort: "relevancia",
        collectionType,
        ...filters,
      };

      const searchParams = new URLSearchParams(
        Object.entries(params).map(([key, value]) => [key, String(value)]),
      );

      const url = `/api/lex/produto/v1/pesquisa/${collectionType.toLowerCase()}?${searchParams.toString()}`;

      const name = currentQueryValue;

      await saveSearch({
        nome: name,
        tipo: collectionType,
        parametros: params,
        url,
      });

      notify(dispatch, {
        message: "Pesquisa salva com sucesso!",
        title: "Sucesso",
        variant: "success",
      });
    } catch (error) {
      const apiMessage =
        error?.response?.data?.erro ||
        error?.response?.data?.message ||
        error?.message;
      const errorMessage = new ErrorMessage({
        message: apiMessage || "Erro ao salvar pesquisa",
        title: "Erro",
        variant: "warning",
      });
      dispatch(addError(errorMessage));
    } finally {
      setIsSavingSearch(false);
    }
  }, [
    appliedFilters,
    appliedModelType,
    currentModelTypeFilters,
    currentPeriod,
    currentQueryValue,
    dispatch,
    modalTypeAppliedFilters,
    searchResultsType,
    searchType,
    selectedPersonFilter,
  ]);

  const renderSearchType = useCallback(() => searchType?.title, [searchType]);

  const currentSearchType = searchType.id;

  const currentAppliedFilterValues =
    appliedFilters[currentSearchType]?.flatMap((filter) => filter.values) ?? [];

  const modalTypeFilterCount =
    currentSearchType !== "modelos" || appliedModelType === "all"
      ? 0
      : modalTypeAppliedFilters?.length + 1;

  const filterCount =
    currentAppliedFilterValues.filter(
      (data) => data !== "code" && data !== "detailed",
    )?.length +
    (currentPeriod?.period && currentPeriod?.period !== "always" ? 1 : 0) +
    (selectedPersonFilter?.length > 2 ? 1 : 0) +
    modalTypeFilterCount;

  useEffect(() => {
    const storedQuery = getStoredSearchQuery();
    if (!currentQueryValue && storedQuery && storedQuery.trim().length > 0) {
      dispatch(setQueryValueRedux(storedQuery));
    }

    const storedSearchType = getStoredSearchType();
    if (storedSearchType && storedSearchType !== searchType.id) {
      dispatch(setSearchType(storedSearchType));
    }
  }, [currentQueryValue, dispatch, searchType.id]);

  useEffect(() => {
    if (
      currentQueryValue &&
      typeof currentQueryValue === "string" &&
      currentQueryValue.trim().length > 0 &&
      currentQueryValue !== lastSyncedReduxValue.current
    ) {
      setQueryValue(currentQueryValue);
      lastSyncedReduxValue.current = currentQueryValue;
    } else if (!currentQueryValue || currentQueryValue === "") {
      lastSyncedReduxValue.current = currentQueryValue || "";
    }
  }, [currentQueryValue, queryValue]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      dispatch(setCurrentInputQuery(queryValue));
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [queryValue, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowHistory(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="pdl_component_omnibar w-100">
      {headerClass !== "home" && (
        <>
          {!isMobile && (
            <div className="header-filter-container d-flex flex-row align-items-center justify-contents-between gap-3">
              <div className="omnibar-search-wrapper position-relative">
                <InputGroup>
                  <DropdownButton
                    variant="outline-secondary"
                    title={renderSearchType()}
                    id="input-group-dropdown-1"
                  >
                    {Object.entries(SEARCH_TYPES)
                      .sort(({ order: a }, { order: b }) => a - b)
                      .map(([id, { title }]) => (
                        <Dropdown.Item
                          href="#"
                          key={`DD_ITEM_${title}`}
                          onClick={() => handleDDChange(id)}
                        >
                          {title}
                        </Dropdown.Item>
                      ))}
                  </DropdownButton>
                  <Form.Control
                    type="text"
                    placeholder={
                      (pageTitle && `Pesquisar ${pageTitle}`) ||
                      "Plataforma LEX"
                    }
                    value={queryValue || ""}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (queryValue && queryValue?.length > 0) {
                        setShowHistory(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowHistory(false), 150);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setShowHistory(false);
                        handleSearch();
                      }
                    }}
                  />
                  <PDLButton
                    variant="tertiary"
                    className="gray px-3"
                    onClick={handleSearch}
                    disabled={queryValue && queryValue?.length < 2}
                  >
                    <SearchSm />
                  </PDLButton>
                  {chatEnabled && (
                    <ChatIaAnimatedButton
                      onClick={() => openChatWithTerm(token, currentQueryValue)}
                    />
                  )}
                </InputGroup>
                <SearchHistoryDropdown
                  show={showHistory}
                  history={history}
                  currentSearchType={searchType.id}
                  onClear={clearHistory}
                  onRemove={removeFromHistory}
                  onSelectSearch={(query) => {
                    dispatch(setCurrentInputQuery(query));
                    setQueryValue(query);
                    setShowHistory(false);
                    dispatch(setQueryValueRedux(query));
                    addToHistory(query, searchType.id);
                    dispatch(doSearch({ query, searchType }));
                    navigate("/resultados");
                  }}
                />
              </div>
              <div className="filter-buttons-container d-flex flex-row align-items-center gap-3">
                <PDLButton
                  variant="primary"
                  onClick={toggleFilterModal}
                  disabled={searchType.id === "all"}
                >
                  <FilterLines />
                  <span className="filter">
                    <p>Filtros</p>
                    {filterCount > 0 && (
                      <p className="count-box">{filterCount}</p>
                    )}
                  </span>
                </PDLButton>

                {showSaveSearchButton && (
                  <PDLButton
                    variant="secondary"
                    onClick={handleSaveSearch}
                    disabled={
                      isSavingSearch ||
                      !currentQueryValue ||
                      currentQueryValue.trim().length === 0
                    }
                  >
                    <Bookmark />
                    <span className="d-none d-sm-block">
                      &nbsp;{isSavingSearch ? "Salvando..." : "Salvar Pesquisa"}
                    </span>
                  </PDLButton>
                )}
              </div>
            </div>
          )}
          {isMobile && (
            <div className="omnibar-search-wrapper position-relative">
              <InputGroup>
                <DropdownButton
                  variant="outline-secondary"
                  title={renderSearchType()}
                  id="input-group-dropdown-mobile"
                  size="sm"
                >
                  {Object.entries(SEARCH_TYPES)
                    .sort(({ order: a }, { order: b }) => a - b)
                    .map(([id, { title }]) => (
                      <Dropdown.Item
                        href="#"
                        key={`DD_ITEM_MOBILE_${title}`}
                        onClick={() => handleDDChange(id)}
                      >
                        {title}
                      </Dropdown.Item>
                    ))}
                </DropdownButton>
                <Form.Control
                  type="text"
                  placeholder={
                    (pageTitle && `Pesquisar ${pageTitle}`) || "Plataforma LEX"
                  }
                  value={queryValue || ""}
                  onChange={handleInputChange}
                  size="sm"
                  onFocus={() => {
                    if (queryValue && queryValue?.length > 0) {
                      setShowHistory(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowHistory(false), 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setShowHistory(false);
                      handleSearch();
                    }
                  }}
                />
                <PDLButton
                  variant="tertiary"
                  className="gray px-2"
                  onClick={handleSearch}
                  size="sm"
                  disabled={queryValue && queryValue?.length < 2}
                >
                  <SearchSm />
                </PDLButton>
                {chatEnabled && (
                  <ChatIaAnimatedButton
                    onClick={() => openChatWithTerm(token, currentQueryValue)}
                  />
                )}
              </InputGroup>
              <SearchHistoryDropdown
                show={showHistory}
                history={history}
                currentSearchType={searchType.id}
                onClear={clearHistory}
                onRemove={removeFromHistory}
                onSelectSearch={(query) => {
                  setQueryValue(query);
                  setShowHistory(false);
                  dispatch(setQueryValueRedux(query));
                  addToHistory(query, searchType.id);
                  dispatch(doSearch({ query, searchType }));
                  navigate("/resultados");
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OmniBar;
